import { Alert, Button, Empty, Segmented, Space, Table, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdOutlineAdd } from 'react-icons/md'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { statusDictionary } from '@/Ship/Components/Filters/StatusFilterBar'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'

import { ACTIVITY_DRAWER, ACTIVITY_RESOURCE, ACTIVITY_STATUS_RESOURCE, OWNER_FIELDS } from '../Contracts/Activity.types'
import { ACTIVITY_FIELD_LABELS as L } from '../Contracts/ActivityFieldLabels'
import { OwnerActivitiesStore } from '../Stores/OwnerActivitiesStore'
import {
    ActivityDeadline,
    ActivityDescription,
    ActivityResponsible,
    ActivityStatusTags,
    activityRowClassName,
} from './ActivityCells'

import type { ActivityOwnerKind, ActivityRecord } from '../Contracts/Activity.types'
import type { TableColumnsType } from 'antd'

/** Власник, у картці якого стоїть список: значення форми власника. */
export interface ActivityOwnerRecord {
    uuid: string
    '@id'?: string
    title?: string
}

export interface ActivityOwnerListProps {
    owner: ActivityOwnerKind
    record: ActivityOwnerRecord
    /** `full` — вкладка «Справи», `compact` — блок на «Загальному» (ліва, широка колонка) */
    mode: 'full' | 'compact'
}

/** Рядків на сторінку: вкладка й вузький блок (`SPEC-011` §8.6, В10). */
const PAGE_SIZE = { full: 10, compact: 5 } as const

const typeCell = (record: ActivityRecord, withDescription: boolean) => {
    const title = typeof record.type?.title === 'string' && record.type.title !== '' ? record.type.title : '—'

    if (!withDescription) return title

    return (
        <Space direction="vertical" size={0}>
            <span>{title}</span>
            {typeof record.description === 'string' && record.description.trim() !== '' ? (
                <Typography.Text type="secondary">
                    <ActivityDescription text={record.description} />
                </Typography.Text>
            ) : null}
        </Space>
    )
}

/**
 * Колонки: ті самі, що в реєстрі, без «Клієнт|Перевізник» — він дублював би
 * власника картки (В9). У вузькому блоці опис — другим рядком під типом (В10).
 */
const columnsFor = (mode: 'full' | 'compact'): TableColumnsType<ActivityRecord> => [
    { title: L.type, key: 'type', render: (_: unknown, record) => typeCell(record, mode === 'compact') },
    ...(mode === 'full'
        ? [
              {
                  title: L.description,
                  key: 'description',
                  render: (_: unknown, record: ActivityRecord) => <ActivityDescription text={record.description} />,
              },
          ]
        : []),
    { title: L.deadline, key: 'deadline', render: (_: unknown, record) => <ActivityDeadline value={record.deadline} /> },
    { title: L.responsible, key: 'responsible', render: (_: unknown, record) => <ActivityResponsible record={record} /> },
    { title: L.status, key: 'status', render: (_: unknown, record) => <ActivityStatusTags record={record} /> },
]

/**
 * Справи власника в його картці (`SPEC-011` §8.6): вкладка «Справи» клієнта й
 * перевізника (`full`) або вузький блок (`compact`).
 *
 *  - **власна таблиця**, а не ядровий список: вкладений `ResourceList` чужого
 *    ресурсу підхоплює контекст картки, і збереження справи пішло б на адресу
 *    власника (`CORE-ISSUES` §3, Р2). Картку справи відкриває
 *    `OpenResourceCardTask` — поза контекстом власника, другою шухлядою, тож
 *    «Зберегти» йде на `/services/activities` (В11);
 *  - **«Нова справа»** — за колекційним `create` справ, а не за правом на
 *    власника: менеджер веде справи клієнта, якого не може редагувати (В8).
 *    Власник у новій справі вже стоїть, перемикача «Клієнт / Перевізник»
 *    немає (`ownerFixed`);
 *  - **рядок статусів** «Всі · Нова · Завершена» з числами — лише у вкладці
 *    (В6); календаря немає (В7); червоний рядок — предикат П;
 *  - список перечитується після закриття картки справи: її могли завершити
 *    чи змінити, не лише створити.
 */
export const ActivityOwnerList = observer(({ owner, record, mode }: ActivityOwnerListProps) => {
    const [store] = useState(() => new OwnerActivitiesStore(owner, PAGE_SIZE[mode], mode === 'full'))
    const dictionary = mode === 'full' ? statusDictionary(ACTIVITY_STATUS_RESOURCE) : null

    useEffect(() => {
        store.bind(record.uuid)
    }, [store, record.uuid])

    useEffect(() => {
        if (dictionary && !dictionary.isLoaded) void dictionary.refresh()
    }, [dictionary])

    const reload = (): void => {
        void store.reload()
    }

    const openCard = (cardMode: 'create' | 'view', uuid?: string): void => {
        const ownerRef = {
            uuid: record.uuid,
            '@id': record['@id'] ?? `/services/${owner === 'client' ? 'clients' : 'carriers'}/${record.uuid}`,
            ...(record.title ? { title: record.title } : {}),
        }

        OpenResourceCardTask({
            resource: ACTIVITY_RESOURCE,
            mode: cardMode,
            displayMode: 'drawer',
            // Ресурсної шухляди `OpenResourceCardTask` не бере (К-1)
            drawer: {
                width: ACTIVITY_DRAWER.width,
                title: cardMode === 'create' ? ACTIVITY_DRAWER.createTitle : ACTIVITY_DRAWER.title,
            },
            ...(uuid ? { primaryFieldValue: uuid } : {}),
            ...(cardMode === 'create'
                ? { initialValues: { [OWNER_FIELDS[owner].owner]: ownerRef, ownerType: owner, ownerFixed: true } }
                : {}),
            onSave: async () => reload(),
            onClose: reload,
        })
    }

    const canAdd = CheckGlobalResourceAccessTask(ACTIVITY_RESOURCE, 'create')

    const countOf = (value: string | undefined): string => {
        if (store.counts === null || store.countsTotal === null) return ''

        return ` ${value === undefined ? store.countsTotal : (store.counts[value] ?? 0)}`
    }

    const statusOptions = dictionary?.isLoaded
        ? [
              { value: '', label: `Всі${countOf(undefined)}` },
              ...dictionary.options.map((option) => ({ value: option.value, label: `${option.label}${countOf(option.value)}` })),
          ]
        : null

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {canAdd ? (
                <Button
                    type="primary"
                    icon={<MdOutlineAdd />}
                    onClick={() => openCard('create')}
                    {...(mode === 'compact' ? { size: 'small' as const } : {})}
                >
                    Нова справа
                </Button>
            ) : null}

            {statusOptions ? (
                <Segmented
                    aria-label="Фільтр за статусом"
                    value={store.status ?? ''}
                    options={statusOptions}
                    onChange={(value) => store.setStatus(value === '' ? undefined : String(value))}
                />
            ) : null}

            {store.failed ? <Alert type="error" showIcon title="Не вдалося завантажити справи." /> : null}

            {!store.isLoading && store.items.length === 0 && !store.failed ? (
                <Empty
                    description="Справ ще немає."
                    {...(mode === 'compact' ? { image: Empty.PRESENTED_IMAGE_SIMPLE } : {})}
                />
            ) : (
                <Table<ActivityRecord>
                    size="small"
                    rowKey={(row) => String(row.uuid)}
                    columns={columnsFor(mode)}
                    dataSource={store.items}
                    loading={store.isLoading}
                    rowClassName={activityRowClassName}
                    pagination={
                        store.total > store.pageSize
                            ? {
                                  current: store.page,
                                  pageSize: store.pageSize,
                                  total: store.total,
                                  showSizeChanger: false,
                                  onChange: (page) => store.setPage(page),
                              }
                            : false
                    }
                    onRow={(row) => ({
                        style: { cursor: 'pointer' },
                        onClick: () => {
                            if (typeof row.uuid === 'string') openCard('view', row.uuid)
                        },
                    })}
                />
            )}
        </Space>
    )
})
